import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DataProvider, StaticDataFetcherItem, DataProviderError } from '@hh.ru/magritte-common-data-provider';
import { DataProviderRequest } from '@hh.ru/magritte-common-data-provider/types';
import { OnOptionsChangeCallback, SelectOption, UseSelectSearch } from '@hh.ru/magritte-ui-select/types';

export const useSelectSearch = <DataType extends StaticDataFetcherItem>(
    visible: boolean,
    selectedValues: DataType[],
    dataProvider: DataProvider<DataType>,
    onOptionsChangeExternal?: OnOptionsChangeCallback<DataType>,
    multiple?: boolean
): UseSelectSearch<DataType> => {
    const [searchValue, setSearchValue] = useState('');
    const [providerOptionList, setProviderOptionList] = useState<SelectOption<DataType>[]>([]);
    const [providerOptionListReady, setProviderOptionListReady] = useState(false);
    const [initOptionList, setInitOptionList] = useState<SelectOption<DataType>[]>([]);
    const [initOptionListReady, setInitOptionListReady] = useState(false);
    const dataProviderOptionsRef = useRef<SelectOption<DataType>[]>([]);
    const emptyResultListRef = useRef<SelectOption<DataType>[]>([]);
    const resultListRef = useRef<SelectOption<DataType>[]>(emptyResultListRef.current);
    const [internalError, setInternalError] = useState(false);
    // для отображения лоадера
    const [optionsLoading, setOptionsLoading] = useState(false);

    // для понимания что сейчас идет поиск, даже если лоадер не показывается
    // чтобы не выводить emptyOptionsSearchHint
    const [searchInProgress, setSearchInProgress] = useState(false);
    const currentRequestRef = useRef<DataProviderRequest>();

    const loadingTimeoutRef = useRef<number>(0);
    const inProgressTimeoutRef = useRef<number>(0);

    const updateOptionsFromProvider = useCallback(
        (text: string) => {
            setInternalError(false);
            setSearchInProgress(true);
            clearTimeout(inProgressTimeoutRef.current);

            // по спеке лоудер на получение данных показывается, если запрос занимает более 1 секунды
            const lastLoadingTimeout = setTimeout(() => {
                if (lastLoadingTimeout === loadingTimeoutRef.current && currentRequestRef.current) {
                    setOptionsLoading(true);
                }
            }, 1000) as unknown as number;
            loadingTimeoutRef.current = lastLoadingTimeout;

            currentRequestRef.current?.cancel();
            const request = dataProvider(text);
            currentRequestRef.current = request;
            request
                .then((result) => {
                    currentRequestRef.current = undefined;
                    const newOptions: SelectOption<DataType>[] = [];
                    result.forEach((section) => {
                        if (section.type === 'delimiter') {
                            newOptions.push({
                                type: 'delimiter',
                                data: { value: section.text, text: section.text } as DataType,
                            });
                        }
                        if (section.type === 'cells') {
                            section.items.forEach((item) => {
                                newOptions.push({
                                    data: item.data ? item.data : ({ text: item.value, value: item.value } as DataType),
                                    disabled: item.disabled,
                                });
                            });
                        }
                    });

                    setProviderOptionList(newOptions);
                    dataProviderOptionsRef.current = newOptions;
                })
                .catch((error) => {
                    setProviderOptionList([]);
                    dataProviderOptionsRef.current = [];
                    // включаем error state если это НЕ ошибка, вызванная `minCharsCount` или отменненым запросом
                    if (!(error instanceof DataProviderError)) {
                        setInternalError(true);
                    }
                })
                .finally(() => {
                    // этот таймаут нужен, чтобы emptyOptionsSearchHint не мигал во время быстрого ввода,
                    // если провайдер работает без дебаунса
                    // без таймаута мы получает очень быстрое переключение состояния на каждый ввод буквы,
                    // а так мы откладываем сброс признака searchInProgress до момента, когда пользователь завершит ввод

                    inProgressTimeoutRef.current = setTimeout(() => {
                        setSearchInProgress(false);
                    }, 300) as unknown as number;
                    clearTimeout(lastLoadingTimeout);
                    setOptionsLoading(false);
                    setProviderOptionListReady(true);
                });
        },
        [dataProvider]
    );

    const updateOptionsFromSelected = useCallback(
        (addOnly = false) => {
            if (addOnly) {
                const addInitOptions = selectedValues
                    .filter((selected) => !initOptionList.find((exist) => exist.data.value === selected.value))
                    .map((item) => ({ data: item }));
                const newInitOptionsList = [...initOptionList, ...addInitOptions];
                setInitOptionList(newInitOptionsList);
            } else {
                const newInitOptionsList = selectedValues.map((item) => ({ data: item }));
                setInitOptionList(newInitOptionsList);
            }
            setInitOptionListReady(true);
        },
        [selectedValues, initOptionList]
    );

    // формируем итоговый список, после того, как сформировали списки из провайдера и из выбранных опций
    // это нужно, чтобы список не прыгал
    // если у нас есть выбранные значения, которых нет в данных провайдера, то мы их подмешиваем в начало списка
    // если итоговый список состоит только из разделителей, то считаем, что список пустой
    const optionList = useMemo(() => {
        let resultList: SelectOption<DataType>[] = emptyResultListRef.current;
        if (providerOptionListReady && initOptionListReady) {
            if (!searchValue) {
                resultList = [
                    ...initOptionList.filter(
                        (selected) =>
                            !providerOptionList.find((provided) => provided.data.value === selected.data.value)
                    ),
                    ...providerOptionList,
                ];
            } else {
                resultList = providerOptionList;
            }
            if (resultList.every((item) => item.type === 'delimiter')) {
                resultList = emptyResultListRef.current;
            }
        }
        if (resultListRef.current !== resultList) {
            resultListRef.current = resultList;
            onOptionsChangeExternal?.(resultList);
        }

        return resultListRef.current;

        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [providerOptionList, providerOptionListReady, initOptionList, initOptionListReady]);

    useEffect(() => {
        if (!visible) {
            setSearchValue('');
            updateOptionsFromSelected();
            updateOptionsFromProvider('');
        }
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [visible]);

    useEffect(() => {
        updateOptionsFromProvider(searchValue);
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [searchValue, dataProvider]);

    useEffect(() => {
        updateOptionsFromSelected(multiple);
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [selectedValues]);

    const reload = useCallback(() => {
        setOptionsLoading(true);
        updateOptionsFromProvider(searchValue);
    }, [updateOptionsFromProvider, searchValue]);

    return {
        optionList,
        searchValue,
        setSearchValue,
        internalLoading: optionsLoading && visible,
        searchInProgress,
        internalError,
        reload,
    };
};
